import * as L from 'leaflet'
import { ShipxyOptions } from '@map-sdk/sdk/config'
import { baselinesAtSeaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/BaselinesAtSeaSymbol'
import { chinaDischargeAreaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/ChinaDischargeAreaSymbol'
import { dischargeAreaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/DischargeAreaSymbol'
import { exclusiveEconomicZoneSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/ExclusiveEconomicZoneSymbol'
import { seaMapToolSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/SeaMapToolSymbol'
import { timeZoneSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/TimeZoneSymbol'
import { globalSeaAreaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/GlobalSeaAreaSymbol'
import { elaneStraitSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/ElaneStraitSymbol'
import { seaRouteSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/SeaRouteSymbol'
import { fisheryManagerSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/FisheryManagerSymbol'
import { earthQuakeAreaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/EarthQuakeAreaSymbol'
import { pirateSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/PirateSymbol'
import { collisionRiskSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/CollisionRiskSymbol'
import { fishAreaSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/FishAreaSymbol'
import { shipAccidentSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/ShipAccidentSymbol'
import { terminatorSymbol } from '@map-sdk/sdk/plugins/AreaView/layer/TerminatorSymbol'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

export interface AreaViewSymbolOptions {
  gsea_url: string
  typ_url: string
  ctyp_url: string
  eez_url: string
  time_url: string
  seamap_url: string
  bline_url: string
  bline12_url: string
  bline24_url: string
  isShowName: boolean
}

interface LayerWithShowArea {
  showArea: () => void
  hideArea: () => void
}

interface LayerWithoutShowArea {
  _isView: boolean
}

type AreaLayer = LayerWithShowArea | LayerWithoutShowArea | L.Layer

class AreaViewSymbol {
  public options: AreaViewSymbolOptions
  protected _map: MyMap
  protected _areaLayer: { [key: string]: AreaLayer }

  constructor(map: MyMap, option?: Partial<AreaViewSymbolOptions>) {
    this.options = {
      gsea_url: '',
      typ_url: '',
      ctyp_url: '',
      eez_url: '',
      time_url: '',
      seamap_url: '',
      bline_url: '',
      bline12_url: '',
      bline24_url: '',
      isShowName: true,
    }

    L.Util.extend(this.options, ShipxyOptions.areaViewSymbol, option)
    this._map = map
    this._areaLayer = {}
  }

  /**
   * 显示，全球海区
   */
  showArea(type: string, data?: any): this {
    if (type) {
      if (!(type in this._areaLayer)) {
        this._areaLayer[type] = this._createArea(type, data)
      }
      const areaLayerType = this._areaLayer[type] as LayerWithShowArea
      if (areaLayerType?.showArea) {
        areaLayerType.showArea()
      }
    }

    return this
  }

  hasView(): boolean {
    for (const i in this._areaLayer) {
      const areaLayerItem = this._areaLayer[i] as LayerWithoutShowArea
      if (areaLayerItem?._isView) {
        return true
      }
    }
    return false
  }

  /**
   * 隐藏，全球海区
   */
  hideArea(type?: string): this {
    if (type) {
      if (type in this._areaLayer) {
        this.areaLayerType(type)
      }
    } else {
      for (const key in this._areaLayer) {
        this.areaLayerType(key)
      }
    }

    return this
  }

  private areaLayerType(key: string) {
    const areaLayerItem = this._areaLayer[key] as LayerWithShowArea &
      LayerWithoutShowArea &
      L.Layer
    if (areaLayerItem?.hideArea) {
      areaLayerItem.hideArea()
    } else if (areaLayerItem) {
      areaLayerItem._isView = false
      this._map.removeLayer(areaLayerItem)
    }
  }

  private _createArea(type: string, data?: any): AreaLayer {
    let layer!: AreaLayer

    switch (type) {
      // 全球海区
      case 'GSEA':
        layer = globalSeaAreaSymbol(this._map, {
          ...this.options,
          url: this.options.gsea_url,
        }) as AreaLayer
        break
      // 排放控制区
      case 'TYP':
        layer = dischargeAreaSymbol(this._map, {
          ...this.options,
          url: this.options.typ_url,
        }) as AreaLayer
        break
      // 排放控制区(中国
      case 'CTYP':
        layer = chinaDischargeAreaSymbol(this._map, {
          ...this.options,
          url: this.options.ctyp_url,
        }) as AreaLayer
        break
      // 专属经济区
      case 'EEZ':
        layer = exclusiveEconomicZoneSymbol(this._map, {
          ...this.options,
          url: this.options.eez_url,
          value: data,
        }) as AreaLayer
        break
      // 时区
      case 'TIME':
        layer = timeZoneSymbol(this._map, {
          ...this.options,
          url: this.options.time_url,
        }) as AreaLayer
        break
      // 临海基线
      case 'BLINE':
        layer = baselinesAtSeaSymbol(this._map, {
          ...this.options,
          url: this.options.bline_url,
          minzoom: 3,
          maxzoom: 18,
        }) as AreaLayer
        break
      case 'BLINE12':
        layer = baselinesAtSeaSymbol(this._map, {
          ...this.options,
          url: this.options.bline12_url,
          minzoom: 3,
          maxzoom: 18,
        }) as AreaLayer
        break
      case 'BLINE24':
        layer = baselinesAtSeaSymbol(this._map, {
          ...this.options,
          url: this.options.bline24_url,
          minzoom: 3,
          maxzoom: 18,
          colors: ['green', 'orange'],
        }) as AreaLayer
        break
      case 'SEAUB':
        layer = seaMapToolSymbol(this._map, {
          ...this.options,
          minzoom: 1,
          maxzoom: 18,
          url: this.options.seamap_url,
        }) as AreaLayer
        break
      // 全球海峡
      case 'QQHX':
        layer = elaneStraitSymbol(this._map) as AreaLayer
        break
      // 沿海航路
      case 'SEA_ROUTE':
        layer = seaRouteSymbol(this._map) as AreaLayer
        break
      // 渔区便捷
      case 'FISHERY_MANAGER':
        layer = fisheryManagerSymbol(this._map) as AreaLayer
        break
      // 海区地震
      case 'EARTH_QUAKE':
        layer = earthQuakeAreaSymbol(this._map) as AreaLayer
        break
      // 海盗事件
      case 'PIRATE':
        layer = pirateSymbol(this._map) as AreaLayer
        break
      //碰撞高风险区
      case 'COLLISION_RISK':
        layer = collisionRiskSymbol(this._map, {
          type: 'collisionRisk',
        }) as AreaLayer
        break
      //碍航网具
      case 'OBSTACLEAREA':
        layer = collisionRiskSymbol(this._map, {
          type: 'obstaclearea',
        }) as AreaLayer
        break
      //渔业协定水域
      case 'FISHAREA':
        layer = fishAreaSymbol(this._map) as AreaLayer
        break
      // 船舶事故
      case 'SHIPACCIDENT':
        layer = shipAccidentSymbol(this._map)
        break
      // 船舶事故
      case 'CBTG':
        layer = terminatorSymbol(this._map) as any
        break
    }

    return layer
  }
}

const areaViewSymbol = (map: MyMap, options?: Partial<AreaViewSymbolOptions>) =>
  new AreaViewSymbol(map, options)

export { AreaViewSymbol, areaViewSymbol }
